import { isEmpty } from 'lodash';
import { storableError } from '../../../../util/errors';
import { createImageVariantConfig } from '../../../../util/sdkLoader';
import { addMarketplaceEntities } from '../../../../ducks/marketplaceData.duck';
import { queryFavorites } from '../FavoritesPage/FavoritesPage.duck';
import { MAX_OF_LISTINGS_IN_ONE_LIST } from '../../config';

// ================ Action types ================ //

export const FETCH_FAVORITE_LISTINGS_REQUEST =
  'app/ViewFavoritePage/FETCH_FAVORITE_LISTINGS_REQUEST';
export const FETCH_FAVORITE_LISTINGS_SUCCESS =
  'app/ViewFavoritePage/FETCH_FAVORITE_LISTINGS_SUCCESS';
export const FETCH_FAVORITE_LISTINGS_ERROR = 'app/ViewFavoritePage/FETCH_FAVORITE_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  queryInProgress: false,
  queryFavoriteListingsError: null,
  currentPageResultIds: [],
};

const resultIds = data => data.data.map(l => l.id);

const ViewFavoritePageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_FAVORITE_LISTINGS_REQUEST:
      return {
        ...state,
        queryInProgress: true,
        queryFavoriteListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_FAVORITE_LISTINGS_SUCCESS:
      return {
        ...state,
        queryInProgress: false,
        currentPageResultIds: payload ? resultIds(payload) : [],
      };
    case FETCH_FAVORITE_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      return {
        ...state,
        queryInProgress: false,
        queryFavoriteListingsError: payload,
        currentPageResultIds: [],
      };

    default:
      return state;
  }
};

export default ViewFavoritePageReducer;

// ================ Action creators ================ //

export const queryFavoriteListingsRequest = () => ({
  type: FETCH_FAVORITE_LISTINGS_REQUEST,
});

export const queryFavoriteListingsSuccess = payload => ({
  type: FETCH_FAVORITE_LISTINGS_SUCCESS,
  payload,
});

export const queryFavoriteListingsError = e => ({
  type: FETCH_FAVORITE_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryFavoriteListings = ({ userId, favoriteId, config }) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(queryFavoriteListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return dispatch(queryFavorites({ userId }))
    .then(async response => {
      const { favoriteLists } = response.data.data.attributes.profile.metadata || {};
      const favoriteList = favoriteLists.find(l => l.id === favoriteId);

      if (!isEmpty(favoriteList?.listingIds)) {
        const queryResponse = await sdk.listings.query({
          ids: favoriteList.listingIds.slice(0, MAX_OF_LISTINGS_IN_ONE_LIST + 1).join(','),
          pub_listingType: config.listing.listingTypes.map(l => l.listingType),
          include: ['author', 'images'],
          'fields.listing': [
            'title',
            'geolocation',
            'price',
            'publicData.listingType',
            'publicData.transactionProcessAlias',
            'publicData.unitType',
            'publicData.location',
            'publicData.producer_type_category',
            'publicData.what_do_you_produce',
            // These help rendering of 'purchase' listings,
            // when transitioning from search page to listing page
            'publicData.pickupEnabled',
            'publicData.shippingEnabled',
          ],
          'fields.user': [
            'profile.displayName',
            'profile.abbreviatedName',
            'profile.publicData.displayName',
          ],
          'fields.image': [
            'variants.scaled-small',
            'variants.scaled-medium',
            `variants.${variantPrefix}`,
            `variants.${variantPrefix}-2x`,
          ],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
          'limit.images': 1,
        });

        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };

        dispatch(addMarketplaceEntities(queryResponse, sanitizeConfig));
        dispatch(queryFavoriteListingsSuccess(queryResponse.data));
        return queryResponse;
      } else {
        dispatch(queryFavoriteListingsSuccess());
      }
    })
    .catch(e => {
      dispatch(queryFavoriteListingsError(storableError(e)));
      throw e;
    });
};

export const loadData = (params, search, config) => (dispatch) => {
  const { userId, favoriteId } = params;

  return dispatch(queryFavoriteListings({
    userId,
    favoriteId,
    config,
  }));
};
